import './styles.css';
import type { LocationConfig } from './types';
import { resolveLocationSlug, loadLocationConfig, saveLocalOverrides, clearLocalOverrides } from './config';
import { Timer, TimerState } from './timer';
import { ensureAudio } from './audio';
import { renderAdminPanel, bindAdminCallbacks, openPanel, closePanel } from './admin';

function renderShell(host: HTMLElement): void {
  host.innerHTML = `
    <div class="stage" id="stage">
      <div class="controls" id="controls">
        <button class="btn" id="startBtn">Start</button>
        <button class="btn" id="pauseBtn">Pause</button>
        <button class="btn" id="skipBtn">Skip</button>
        <button class="btn" id="resetBtn">Reset</button>
        <button class="btn" id="fsBtn">Fullscreen</button>
        <button class="btn primary" id="adminBtn">Settings</button>
      </div>

      <header>
        <div class="brand">
          <img class="brand-logo" src="/brand/logo-horizontal-white.png" alt="Dill Dinkers Pickleball" />
        </div>
        <div class="location">
          <div class="name" id="locName"></div>
          <div class="sub" id="locSub"></div>
        </div>
      </header>

      <main>
        <div class="interval-label" id="intervalLabel"></div>
        <div class="timer" id="timer">00:00</div>
        <div class="progress"><span id="progressBar" style="width: 100%"></span></div>
        <div class="interval-counter" id="intervalCounter"></div>
      </main>

      <footer>
        <div class="ticker"><div class="ticker-inner" id="tickerInner"></div></div>
        <img class="tagline-img" src="/brand/tagline-white.png" alt="Play Safe. Play Well." />
      </footer>
    </div>
  `;
  renderAdminPanel(host);
}

function fmtTime(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
  return `${String(m).padStart(2,'0')}:${String(sec).padStart(2,'0')}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]!));
}

class App {
  private cfg: LocationConfig;
  private timer: Timer;
  private el!: {
    timer: HTMLElement; label: HTMLElement; counter: HTMLElement;
    progress: HTMLElement; locName: HTMLElement; locSub: HTMLElement;
    tickerInner: HTMLElement;
  };

  constructor(cfg: LocationConfig) {
    this.cfg = cfg;
    this.timer = new Timer(cfg, {
      onTick: (s) => this.render(s),
      onIntervalEnd: () => { /* phase 2 hook */ }
    });
  }

  mount(): void {
    this.el = {
      timer: document.getElementById('timer')!,
      label: document.getElementById('intervalLabel')!,
      counter: document.getElementById('intervalCounter')!,
      progress: document.getElementById('progressBar')!,
      locName: document.getElementById('locName')!,
      locSub: document.getElementById('locSub')!,
      tickerInner: document.getElementById('tickerInner')!
    };
    this.applyConfig();
    this.bindEvents();
    this.render(this.timer.getState());
  }

  private applyConfig(): void {
    document.title = `${this.cfg.location.name} — Dill Dinkers Timer`;
    document.body.className = 'theme-' + this.cfg.theme;
    this.el.locName.textContent = this.cfg.location.name;
    this.el.locSub.textContent = this.cfg.location.sub;
    this.el.tickerInner.innerHTML = this.cfg.ticker
      .map(t => `<span>${escapeHtml(t)}</span>`).join('');
  }

  private render(state: TimerState): void {
    const interval = this.timer.currentInterval();
    this.el.timer.textContent = fmtTime(state.remainingMs);
    this.el.label.textContent = interval.label;
    this.el.counter.textContent =
      `Interval ${state.intervalIdx + 1} of ${this.cfg.schedule.intervals.length} · Cycle ${state.completedCycles + 1}`;
    const totalMs = interval.durationSec * 1000;
    const pct = (state.remainingMs / totalMs) * 100;
    this.el.progress.style.width = pct + '%';
    this.updateColor(state, pct);
  }

  private updateColor(state: TimerState, pct: number): void {
    const t = this.cfg.colorThresholds;
    let color: string;
    if (pct >= t.greenAbovePct) color = 'var(--good)';
    else if (pct >= t.yellowAbovePct) color = 'var(--warn)';
    else color = 'var(--bad)';
    document.documentElement.style.setProperty('--timer-color', color);
    const secLeft = state.remainingMs / 1000;
    this.el.timer.classList.toggle('pulse', secLeft <= t.pulseBelowSec && state.running);
  }

  private bindEvents(): void {
    const $ = (id: string) => document.getElementById(id)!;

    $('startBtn').addEventListener('click', () => { ensureAudio(); this.timer.start(); });
    $('pauseBtn').addEventListener('click', () => this.timer.pause());
    $('skipBtn').addEventListener('click', () => this.timer.skip());
    $('resetBtn').addEventListener('click', () => this.timer.reset());
    $('fsBtn').addEventListener('click', toggleFullscreen);
    $('adminBtn').addEventListener('click', () => openPanel(this.cfg));
    $('closePanel').addEventListener('click', closePanel);

    bindAdminCallbacks(
      (updates) => {
        this.cfg = { ...this.cfg, ...updates } as LocationConfig;
        saveLocalOverrides(this.cfg);
        this.timer.updateConfig(this.cfg);
        this.applyConfig();
        closePanel();
        showToast('Settings saved');
      },
      () => {
        clearLocalOverrides(this.cfg.slug);
        location.reload();
      }
    );

    document.addEventListener('keydown', (e) => {
      const t = e.target as HTMLElement;
      if (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') return;
      if (e.code === 'Space') {
        e.preventDefault();
        if (this.timer.getState().running) this.timer.pause();
        else { ensureAudio(); this.timer.start(); }
      }
      const k = e.key.toLowerCase();
      if (k === 'r') this.timer.reset();
      if (k === 'n') this.timer.skip();
      if (k === 'f') toggleFullscreen();
      if (k === 'a') openPanel(this.cfg);
      if (e.key === 'Escape') closePanel();
    });

    const controls = document.getElementById('controls')!;
    let hideTimer: number | undefined;
    const showControls = () => {
      controls.classList.add('visible');
      clearTimeout(hideTimer);
      hideTimer = window.setTimeout(() => controls.classList.remove('visible'), 3000);
    };
    document.addEventListener('mousemove', showControls);
    document.addEventListener('touchstart', showControls);
  }
}

function toggleFullscreen(): void {
  if (!document.fullscreenElement) document.documentElement.requestFullscreen();
  else document.exitFullscreen();
}

function showToast(msg: string): void {
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = msg;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2400);
}

async function boot(): Promise<void> {
  const host = document.getElementById('app')!;
  renderShell(host);

  const slug = resolveLocationSlug();
  try {
    const cfg = await loadLocationConfig(slug);
    new App(cfg).mount();
  } catch (err) {
    host.innerHTML = `<div style="padding:48px;font-family:Inter,sans-serif;color:#fff">
      <h1>Couldn't load location</h1>
      <p>${(err as Error).message}</p></div>`;
  }
}

boot();
